import { useCallback, useEffect, useState } from 'react';
import { StickyContainer } from 'react-sticky';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import cx from 'classnames';
import Media from 'react-media';

import { Footer } from '@/components/shop/Footer/Footer';
import { receiveUser } from '@zola-helpers/client/dist/es/redux/user/userActions';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import { isUserPostWedding } from '@zola-helpers/client/dist/es/util/dateUtils';
import { SecondaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';
import Logger from '@zola-helpers/server/dist/es/logger';
import type {
  WProductRecommendationsView,
  WProductView,
  WSkuPreview,
} from '@zola/svc-web-api-ts-client';

import { getUserContext } from '@/lib/server/api/user';
import { initializeStore } from '@/store/nextStore';

import ModalRoot from '@/components/common/ModalRoot';
import Notifications from '@/components/common/notifications/Notifications';
import ToastsContainer from '@/components/common/ToastsContainer';
import usePDPActiveNavItems from '@/hooks/usePDPActiveNavItems';
import '@/pages/ProductDetailPage/ProductDetailPage.less';
import '@/pages/ProductDetailPage/ProductOverviewRow.less';
import ProductsCarousel from '@/pages/ProductDetailPage/ProductsCarousel';
import ClientApp from '@/ClientApp';
import ClientRouter from '@/ClientRouter';
import UnifiedNavV2 from '@/components/common/header/UnifiedNavV2';
import { CarouselBreadCrumb } from '@/pages/ProductDetailPage/CarouselBreadCrumb';
import Header from '@/pages/ProductDetailPage/Header';
import { BreadCrumbs, BreadCrumbSkeleton } from '@/pages/ProductDetailPage/BreadCrumb';
import ProductDetailReview from '@/pages/ProductDetailPage/ProductDetailReview';
import ProductOverview from '@/pages/ProductDetailPage/ProductOverview';
import ProductDetailRow from '@/pages/ProductDetailPage/ProductDetailRow';
import TabPanel from '@/pages/ProductDetailPage/TabPanel';

import { getProductByLookKey } from '@/lib/server/api/product';
import {
  findFirstImage,
  getProductAddedEventData,
  getProductViewedEventData,
  getShipDates,
  SCREEN_SIZE_XS,
  setSkuAttributes,
} from '@/pages/ProductDetailPage/helpers';
import { renderProductSchema } from '@/pages/ProductDetailPage/util';
import { resolveMatchingSku } from '@/util/resolveMatchingSku';
import { onAddToCart } from '@/pages/ProductDetailPage/onAddToCart';

import { useAppDispatch, useAppSelector } from '@/store';
import { addItemToCart, AddToCartResponse } from '@/actions/CartActions';
import { trackProductAdded, trackProductViewed } from '@/util/trackingHelper';
import { getAvailableSkuPersonalization } from '@/actions/PersonalizationActions';
import { showModal } from '@/actions/ModalActions';
import { fetchRecommendedProducts, fetchRelatedProducts } from '@/actions/ProductDetailPageActions';
import { AddToCartCallbackParams } from '@/pages/ProductDetailPage/types';

import { RegistryPage } from '@/contexts/shop/RegistryPage';
import { serverIsBaby } from 'server/lib/next/isBaby';
import { getBabyProductByLookKey } from '@/lib/server/api/baby';
import { findProductLookByKey, findSkuById } from '@/pages/ProductDetailPage/pdpHelpers';
import { getBabyNavHtml } from '@/lib/server/api/nav';

const MobileCta = dynamic(import('@/pages/ProductDetailPage/MobileCta'), {
  ssr: false,
});

type ProductDetailPageProps = {
  userContext: UserContext | null;
  productLookKey: string;
  productData: WProductView | null;
  showCustomizationModal: boolean;
  fromChecklistQueryParam?: string;
  skuId?: string;
  navHtml: string | null;
  isBaby: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
};

const ProductDetailPage = (props: ProductDetailPageProps): JSX.Element => {
  const {
    userContext,
    productLookKey,
    productData,
    fromChecklistQueryParam,
    showCustomizationModal,
    skuId,
    isBaby,
    navHtml,
  } = props;
  const dispatch = useAppDispatch();

  const productLooks = productData?.product_look_views;
  // try to find the matching look by key, otherwise choose the first one
  const selectedProductLook = productData
    ? findProductLookByKey(productLookKey, productLooks) || productLooks?.[0]
    : undefined;

  const [activeProductLook, setActiveProductLook] = useState(selectedProductLook);
  const [productAttributes, setProductAttributes] = useState({});
  const [activeSku, setActiveSku] = useState<WSkuPreview | null>(
    (skuId
      ? findSkuById(selectedProductLook?.sku_previews || [], skuId)
      : resolveMatchingSku(selectedProductLook?.sku_previews || [], {})) ?? null
  );
  const [shipDates, setShipDates] = useState(getShipDates(activeSku));
  const [quantity, setQuantity] = useState(productData?.suggested_quantity || 1);
  const [associatedProducts, setAssociatedProducts] = useState<WProductRecommendationsView>({});
  const [kittedProducts, setKittedProducts] = useState<WProductView[]>([]);
  const [brandProducts, setBrandProducts] = useState<WProductView[]>([]);
  const [isProductViewed, setIsProductViewed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openPersonalizationModal = useCallback(
    (event?: React.MouseEvent) => {
      if (event) event.preventDefault();
      if (activeSku && activeSku.id) {
        dispatch(
          getAvailableSkuPersonalization(
            activeSku.id,
            productData?.name,
            productData?.brand && productData?.brand.name
          )
        ).then(() => dispatch(showModal('PERSONALIZATION_MODAL')));
      }
    },
    [activeSku, productData, dispatch]
  );

  useEffect(() => {
    if (skuId && activeSku) {
      setSkuAttributes(setProductAttributes, productData, activeSku);
    }
  }, [skuId, activeSku, productData]);

  useEffect(() => {
    // if we have an active SKU, set shipping dates
    if (activeSku) {
      const dates = getShipDates(activeSku);
      setShipDates(dates);
      setSkuAttributes(setProductAttributes, productData, activeSku);
    }

    if (activeProductLook) {
      // we need some kind of SKU here, so use the active one if it exists, otherwise choose the first one
      const defaultSku = activeSku || activeProductLook?.sku_previews?.[0];
      const productId = productData?.id;
      const skuId = defaultSku?.id;

      if (productId) {
        fetchRelatedProducts({ productId }).then((res) => {
          setKittedProducts(res.kitted_products || []);
          setBrandProducts(res.featured_products || []);
        });
      }

      if (productId && skuId) {
        const associatedOptions = {
          productId,
          skuId,
          type: ['also-added-skus', 'frequently-added-together-skus'],
        };
        fetchRecommendedProducts(associatedOptions).then((res) => {
          setAssociatedProducts(res);
        });
      }

      // track product viewed
      const eventData = getProductViewedEventData(
        productData,
        activeProductLook,
        defaultSku,
        quantity
      );
      if (!isProductViewed) {
        trackProductViewed(eventData);
        setIsProductViewed(true);
      }

      const personalizable = activeSku && productData?.personalizable;
      if (personalizable && !isModalOpen && showCustomizationModal) {
        openPersonalizationModal();
        setIsModalOpen(true);
      }
    }
  }, [
    activeProductLook,
    activeSku,
    dispatch,
    isModalOpen,
    isProductViewed,
    openPersonalizationModal,
    productData,
    quantity,
    showCustomizationModal,
  ]);

  const addToCartCallback = useCallback(
    (response: AddToCartResponse, requestQuantity: number) => {
      // checking for target to ensure that it's the main CTA calling this function
      if (response && response.payload) {
        const { payload } = response;
        const eventData = getProductAddedEventData(
          productData,
          activeProductLook,
          activeSku,
          requestQuantity,
          payload.cart_id
        );
        trackProductAdded(eventData);
      }
      window.dispatchEvent(new CustomEvent('OPEN_MODAL_EVENT', { detail: 'CART' }));
    },
    [productData, activeProductLook, activeSku]
  );

  const addToCartParams: AddToCartCallbackParams = {
    activeSku,
    quantity,
    setQuantity,
    dispatch,
    addItemToCart,
    personalizationData: null,
  };

  const customizedInputsData = useAppSelector(
    (state) => state.personalization.customizedInputsData
  );
  const savedPersonalization = useAppSelector(
    (state) => state.personalization.savedPersonalization
  );
  const personalizedImageUrl = savedPersonalization?.personalizations?.[0]?.image_url;

  if (savedPersonalization && savedPersonalization.sku_id) {
    const personalizationData = {
      uuid: savedPersonalization.uuid,
      sku_id: savedPersonalization.sku_id,
    };
    addToCartParams.personalizationData = personalizationData;
  }

  const handleAddToCart = onAddToCart(addToCartParams, addToCartCallback);
  const isPersonalizationRequired =
    activeSku && activeSku.must_be_personalized && !customizedInputsData;

  const productBrand = productData?.brand?.name || '';
  const productCommonCanonicalKey = productData?.swatches?.[0]?.product_look_key;
  const productImage = productData && findFirstImage(productData);
  const partnerRetailerKey = productData?.partner_retailer_view?.key;

  const isGuest = userContext ? Boolean(userContext.is_guest) : true;

  const { activePrimaryNav, activeSecondaryNavKey, isWeddingBoutiquePage } =
    usePDPActiveNavItems(productData);
  let adjustedActiveSecondaryLink = activeSecondaryNavKey;
  if (!isGuest) {
    adjustedActiveSecondaryLink = isUserPostWedding(userContext || undefined)
      ? SecondaryNavIdentifier.POST_WEDDING_SHOP
      : SecondaryNavIdentifier.ADD_GIFTS;
  }

  return (
    <RegistryPage isBaby={isBaby} userContext={userContext}>
      <div
        className={cx({
          postAuthNav3PageBackground: !isGuest,
        })}
      >
        <StickyContainer
          className={cx({
            postAuthNav3PageWrapper: !isGuest,
          })}
        >
          {productData && (
            <Header
              productBrand={productBrand}
              productKey={productLookKey}
              productCanonicalKey={productCommonCanonicalKey}
              productTitle={productData.display_name}
              productDescription={productData.description}
              productImage={productImage}
              partnerRetailerKey={partnerRetailerKey}
            />
          )}

          {isBaby ? (
            <div
              id="root-nav-container"
              data-testid="root-nav"
              dangerouslySetInnerHTML={{ __html: navHtml || '' }}
            />
          ) : (
            <UnifiedNavV2
              showPromo
              activePrimaryLink={activePrimaryNav}
              activeSecondaryLink={adjustedActiveSecondaryLink}
              activeTertiaryLink={activeSecondaryNavKey}
              isWeddingBoutiquePage={isWeddingBoutiquePage}
            />
          )}
          <div
            className={cx({
              'product-detail-outer-container--isPostAuthNav3': !isGuest,
            })}
          >
            <div className={cx({ 'product-detail-inner-container': !isGuest })}>
              <BreadCrumbSkeleton type="desktop" />
              <BreadCrumbSkeleton type="tablet" />
              <div itemScope itemType="https://schema.org/Product">
                {renderProductSchema({
                  activeProductLook,
                  activeSku,
                  productImage,
                  productKey: productLookKey,
                  productBrand,
                })}
                {productData && <meta itemProp="name" content={productData.name} />}
                <div className="product-detail-page_container">
                  {/* 1200px and smaller breadcrumbs */}
                  {productData && activeProductLook && (
                    <BreadCrumbs
                      crumbKey={activeProductLook.key}
                      productData={productData}
                      className="hide-screen-lg"
                      fromChecklistQueryParam={fromChecklistQueryParam}
                    />
                  )}
                  {/* 1200px+ breadcrumbs and the image carousel (shown at all breakpoints) */}
                  <CarouselBreadCrumb
                    productData={productData}
                    activeProductLook={activeProductLook}
                    personalizedImageUrl={personalizedImageUrl}
                    fromChecklistQueryParam={fromChecklistQueryParam}
                  />
                  <ProductOverview
                    shipDates={shipDates}
                    productData={productData}
                    activeProductLook={activeProductLook}
                    setActiveProductLook={setActiveProductLook}
                    productAttributes={productAttributes}
                    setProductAttributes={setProductAttributes}
                    activeSku={activeSku}
                    setActiveSku={setActiveSku}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    handleAddToCart={handleAddToCart}
                    openPersonalizationModal={openPersonalizationModal}
                    isPersonalizationRequired={isPersonalizationRequired}
                    userContext={userContext}
                  />
                </div>
                <TabPanel
                  shipDates={shipDates}
                  productData={productData}
                  activeProductLook={activeProductLook}
                  activeSku={activeSku}
                  productAttributes={productAttributes}
                  type="tablet"
                />
                {kittedProducts.length > 0 && (
                  <div className="also-need-products_container">
                    <h2 id="also-need-text" className="also-need-text">
                      You May Also Need
                    </h2>
                    {kittedProducts.map((product, i) => (
                      <ProductDetailRow
                        key={i}
                        position={i}
                        location="PDP"
                        section="KIT"
                        userContext={userContext}
                        dispatch={dispatch}
                        productData={product}
                      />
                    ))}
                  </div>
                )}
                <Media query={`(max-width: ${SCREEN_SIZE_XS})`}>
                  <MobileCta
                    handleAddToCart={handleAddToCart}
                    activeProductLook={activeProductLook}
                    activeSku={activeSku}
                    quantity={quantity}
                    userContext={userContext}
                    productData={productData}
                    productAttributes={productAttributes}
                    openPersonalizationModal={openPersonalizationModal}
                    isPersonalizationRequired={isPersonalizationRequired}
                  />
                </Media>
                <ClientApp>
                  <ClientRouter>
                    {associatedProducts.type && (
                      <ProductsCarousel
                        productData={productData}
                        products={associatedProducts.products}
                        type={associatedProducts.type}
                        endpointVariant={associatedProducts.endpoint_variant}
                        dispatch={dispatch}
                        addItem={addItemToCart}
                        userContext={userContext}
                        location="PDP"
                        section="RECOMMENDATION_ALSO_ADDED"
                      />
                    )}
                    {brandProducts.length > 0 && (
                      <ProductsCarousel
                        productData={productData}
                        products={brandProducts}
                        dispatch={dispatch}
                        addItem={addItemToCart}
                        type="brand-related"
                        location="PDP"
                        section="BRAND"
                      />
                    )}
                  </ClientRouter>
                </ClientApp>
                {productData && <ProductDetailReview productId={productData.id} />}
              </div>
            </div>
            <Footer userContext={userContext} />
          </div>
        </StickyContainer>
      </div>
      <ModalRoot />
      <Notifications />
      <ToastsContainer />
    </RegistryPage>
  );
};

export const getServerSideProps: GetServerSideProps<ProductDetailPageProps> = async (context) => {
  const isBabyPage = serverIsBaby(context);

  const reduxStore = initializeStore();
  const { dispatch, getState } = reduxStore;

  const { productKey } = context.params || {};
  const { fromChecklist, show_modal: showCustomizationModal, skuId } = context.query;

  let productData: WProductView | null = null;
  let userContext: UserContext | null = null;
  let navHtml: string | null = null;

  const [productDataResponse, userContextResponse, navResponse] = await Promise.allSettled([
    isBabyPage
      ? getBabyProductByLookKey(productKey as string)
      : getProductByLookKey(productKey as string),
    getUserContext(context.req),
    isBabyPage ? getBabyNavHtml(context.req) : Promise.resolve(null),
  ]);

  if (userContextResponse.status === 'fulfilled') {
    userContext = userContextResponse.value;
  }
  if (userContext) {
    dispatch(receiveUser(userContext));
  }

  if (productDataResponse.status === 'fulfilled') {
    productData = productDataResponse.value;
  } else {
    // Handle expected errors more gracefully
    if (
      productDataResponse.reason.response?.error?.category?.domain === 'product' ||
      productDataResponse.reason.response?.error?.category?.reason === 'not-found'
    ) {
      Logger.debug(productDataResponse.reason.response?.error?.message);
    }
    Logger.error(productDataResponse.reason.response?.error?.message);

    // Return a user friendly 404 page in any case
    return {
      notFound: true,
    };
  }

  if (navResponse.status === 'fulfilled') {
    navHtml = navResponse.value;
  }

  const initialReduxState = JSON.stringify(getState());

  return {
    props: {
      userContext,
      initialReduxState,
      productData: productData || null,
      productLookKey: productKey as string,
      fromChecklistQueryParam: (fromChecklist as string) || '',
      showCustomizationModal: !!showCustomizationModal,
      skuId: (skuId as string) || '',
      isBaby: isBabyPage,
      navHtml,
    },
  };
};

export default ProductDetailPage;
